"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTextNode, $insertNodes } from "lexical";
import MobileWrapperDialog from "../mobile-wrapper-dialog/MobileWrapperDialog";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { useEmoji } from "@/contexts/EmojiContext";
import { useCreateDropEmojiPickerLayer } from "./CreateDropEmojiPickerLayerContext";
interface CreateDropEmojiPickerProps {
  top?: string | undefined;
}
const CreateDropEmojiPicker: FC<CreateDropEmojiPickerProps> = t0 => {
  const $ = _c(36);
  const {
    top: t1
  } = t0;
  const top = t1 === undefined ? "tw-top-2" : t1;
  const isMobile = useIsMobileScreen();
  const {
    desktopZIndex,
    mobileZIndexClassName
  } = useCreateDropEmojiPickerLayer();
  const {
    emojiMap,
    categories,
    categoryIcons
  } = useEmoji();
  const [editor] = useLexicalComposerContext();
  const [showPicker, setShowPicker] = useState(false);
  const buttonRef = useRef(null);
  const pickerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      top: 0,
      left: 0
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [pickerPosition, setPickerPosition] = useState(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const button = buttonRef.current;
      if (!button) {
        return null;
      }
      const rect = button.getBoundingClientRect();
      return {
        top: rect.top + globalThis.scrollY - 420,
        left: rect.left + globalThis.scrollX - 250
      };
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const getPickerPosition = t3;
  let t4;
  if ($[2] !== showPicker) {
    t4 = () => {
      if (showPicker) {
        setShowPicker(false);
        return;
      }
      const nextPickerPosition = getPickerPosition();
      if (nextPickerPosition) {
        setPickerPosition(nextPickerPosition);
      }
      setShowPicker(true);
    };
    $[2] = showPicker;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleTogglePicker = t4;
  let t5;
  if ($[4] !== editor) {
    t5 = emoji => {
      let emojiText = emoji.native;
      if (!emojiText && emoji.id) {
        emojiText = `:${emoji.id}:`;
      }
      if (emojiText) {
        editor.update(() => {
          const emojiNode = $createTextNode(emojiText);
          $insertNodes([emojiNode]);
        });
        requestAnimationFrame(() => {
          editor.focus();
          editor.update(_temp);
        });
      }
      setShowPicker(false);
    };
    $[4] = editor;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const addEmoji = t5;
  let t6;
  let t7;
  if ($[6] !== showPicker) {
    t6 = () => {
      const handleClickOutside = event => {
        if (pickerRef.current && !pickerRef.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
          setShowPicker(false);
        }
      };
      if (showPicker) {
        document.addEventListener("mousedown", handleClickOutside);
      } else {
        document.removeEventListener("mousedown", handleClickOutside);
      }
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t7 = [showPicker];
    $[6] = showPicker;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  const t8 = `tw-absolute tw-right-2 ${top} tw-flex tw-items-start tw-justify-center`;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <svg className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-transition tw-duration-300 tw-ease-out" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><g transform="scale(0.024)"><path d="M958 104h-62V43q0-17-12-29T855 2h-87q-17 0-29 12t-12 29v61h-62q-17 0-29 12t-12 29v4q-81-34-169-34-116 0-217 59-97 57-154 154-58 100-58 216.5T84 761q57 98 154 155 101 58 217.5 58T672 916q97-57 154-155 59-100 59-216 0-88-34-168h4q17 0 29-12t12-29v-62h62q17 0 29-12t12-29v-88q0-17-12-29t-29-12zM644 348q29 0 49.5 20.5t20.5 49-20.5 49T644 487t-49.5-20.5-20.5-49 20.5-49T644 348zm-377 0q29 0 49.5 20.5t20.5 49-20.5 49T267 487t-49.5-20.5-20.5-49 20.5-49T267 348zm473 255q-10 70-50.5 126.5t-102 88.5-132 32-132-32-102-88.5T171 603q-2-16 8.5-27.5T206 564h499q16 0 26.5 11.5T740 603zm218-370H855v103h-87V233H665v-88h103V43h87v102h103v88z" /></g></svg>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== handleTogglePicker) {
    t10 = <button ref={buttonRef} className="tw-flex tw-items-center tw-justify-center tw-rounded tw-border-none tw-bg-transparent tw-p-[0.35rem] tw-opacity-50 tw-transition tw-duration-150 hover:tw-bg-[rgb(40,40,40)] hover:tw-text-[#FFCC22] hover:tw-opacity-100" onClick={handleTogglePicker}>{t9}</button>;
    $[10] = handleTogglePicker;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== addEmoji || $[13] !== categories || $[14] !== categoryIcons || $[15] !== desktopZIndex || $[16] !== emojiMap || $[17] !== isMobile || $[18] !== pickerPosition || $[19] !== showPicker) {
    t11 = !isMobile && showPicker && createPortal(<div ref={pickerRef} style={{
      position: "absolute",
      top: pickerPosition.top,
      left: pickerPosition.left,
      zIndex: desktopZIndex
    }} className="tw-rounded-lg tw-border tw-bg-iron-800 tw-p-[1px] tw-shadow-lg"><Picker theme="dark" data={data} onEmojiSelect={addEmoji} custom={emojiMap} categories={categories} categoryIcons={categoryIcons} /></div>, document.body);
    $[12] = addEmoji;
    $[13] = categories;
    $[14] = categoryIcons;
    $[15] = desktopZIndex;
    $[16] = emojiMap;
    $[17] = isMobile;
    $[18] = pickerPosition;
    $[19] = showPicker;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t8) {
    t12 = <div className={t8}>{t10}{t11}</div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t8;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== addEmoji || $[26] !== categories || $[27] !== categoryIcons || $[28] !== emojiMap || $[29] !== isMobile || $[30] !== mobileZIndexClassName || $[31] !== showPicker) {
    t13 = isMobile && <MobileWrapperDialog isOpen={showPicker} onClose={() => setShowPicker(false)} zIndexClassName={mobileZIndexClassName}><div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center" onTouchMove={_temp2}><Picker theme="dark" data={data} onEmojiSelect={addEmoji} custom={emojiMap} categories={categories} categoryIcons={categoryIcons} /></div></MobileWrapperDialog>;
    $[25] = addEmoji;
    $[26] = categories;
    $[27] = categoryIcons;
    $[28] = emojiMap;
    $[29] = isMobile;
    $[30] = mobileZIndexClassName;
    $[31] = showPicker;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};
export default CreateDropEmojiPicker;
function _temp() {}
function _temp2(e) {
  return e.stopPropagation();
}